// Единственная точка выхода в сеть. Экраны сюда не лезут напрямую — только через хуки.
// Токена ядра здесь НЕТ: браузер ходит в свой BFF по сессионной куке.
import { запомнитьЗону, источник, сделкиМожноИзСвоей } from './istochnik'
import { переводКомпаний, переводКонтактов, переводСделок } from './own'
import type {
  Company, Contact, CrmItem, Deal, FieldsData, HealthInfo, ListParams, ListResult, Me, RefsData,
  ScopeNote, User, Vessel,
} from './types'

/** 401 от BFF — сессия кончилась. Экраны редиректят на /login. */
export class Unauthorized extends Error {
  constructor() {
    super('unauthorized')
    this.name = 'Unauthorized'
  }
}

/** Ошибка ядра/BFF с человекочитаемым текстом. */
export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Ядро честно ответило «не умею» (`_core_unsupported`, HTTP 501): например фильтр по полю,
 * которое лежит в raw строкой неподходящего формата. Это НЕ «ничего не найдено»: пустой
 * список здесь означал бы «долгов нет», поэтому у отказа свой тип — экран обязан сказать
 * пользователю правду, а не показать пустую таблицу.
 */
export class CoreUnsupported extends Error {
  why: string
  constructor(why: string) {
    super(why ? `ядро не умеет: ${why}` : 'ядро не умеет обслужить этот запрос')
    this.name = 'CoreUnsupported'
    this.why = why
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',            // сессионная кука
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  })
  if (res.status === 401) throw new Unauthorized()
  const text = await res.text()
  const data: unknown = text ? JSON.parse(text) : {}
  if (data && typeof data === 'object' && (data as Record<string, unknown>)._core_unsupported) {
    throw new CoreUnsupported(String((data as Record<string, unknown>).why ?? ''))
  }
  if (!res.ok) {
    const rec = data as Record<string, unknown>
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new ApiError(msg, res.status)
  }
  return data as T
}

// ─────────── auth ───────────
export const login = (username: string, password: string) =>
  request<{ ok: boolean; user: User }>('/api/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })

export const logout = () => request<{ ok: boolean }>('/api/logout', { method: 'POST' })

/**
 * Единый вход СиР (`cir-id`): что о нём известно браузеру.
 *
 * `enabled` — включён ли он на сервере (`CIR_ID_ENABLED` в .env). `cookie` — лежит ли у
 * человека общая кука `.cirspb.com`, то есть заходил ли он уже на портал СиР. Больше
 * браузеру знать и не надо: личность проверяет BFF, а здесь решается один вопрос —
 * показывать ли на странице входа кнопку «Войти через порталы СиР».
 */
export type SsoInfo = { enabled: boolean; cookie: boolean }

/** Отдельным запросом, а не через fetchMe: форма Me — чужой контракт, её не трогаем. */
export const fetchSso = async (): Promise<SsoInfo> => {
  try {
    const res = await fetch('/api/me', { credentials: 'same-origin' })
    if (!res.ok) return { enabled: false, cookie: false }
    const data = (await res.json()) as { sso?: Partial<SsoInfo> }
    return { enabled: !!data.sso?.enabled, cookie: !!data.sso?.cookie }
  } catch {
    // BFF не ответил — просто не предлагаем единый вход. Форма с паролем на месте.
    return { enabled: false, cookie: false }
  }
}

/**
 * Войти по общей куке cir-id. Пароля здесь нет вовсе: личность приносит подписанная кука,
 * BFF проверяет её у самой cir-id и заводит обычную сессию CRM.
 *
 * Обычно этого запроса не требуется — `/api/me` подхватывает куку сам. Кнопка нужна после
 * выхода: BFF помнит «этот человек вышел» и молчит, пока его явно не попросят.
 */
export const ssoCirId = () =>
  request<{ ok: boolean; user: User }>('/api/sso/cir-id', { method: 'POST' })

/**
 * Кто я и что мне видно. Не бросает 401 — возвращает `{user: null, rights: null}`
 * для незалогиненного (на этом держится RequireAuth).
 *
 * Возвращаем ответ ЦЕЛИКОМ, а не одного `user`. Раньше здесь стояло `return data.user`,
 * и всё, что BFF рассказывает о правах (зона, предупреждения, закрытые разделы, право
 * записи), выбрасывалось на границе сети. Дороже всего терялось предупреждение «Учётка
 * не связана с сотрудником Bitrix»: человек видел пустую CRM и решал, что данных нет.
 *
 * Старый BFF (или заглушка) может не присылать `rights` — тогда там null, и интерфейс
 * НИЧЕГО не прячет. Это правильная сторона для ошибки: подсказка отсутствует, а не
 * запрещает, а настоящее ограничение всё равно накладывает сервер.
 */
export const fetchMe = async (): Promise<Me> => {
  const res = await fetch('/api/me', { credentials: 'same-origin' })
  if (!res.ok) return { user: null, rights: null }
  const data = (await res.json()) as Partial<Me>
  // Предохранитель источника сделок (см. istochnik.ts): пока /api/own не сужает выборку
  // зоной, учётки с границами читают сделки из зеркала, где сужение стоит.
  запомнитьЗону(data.rights?.scope?.kind ?? null)
  return { user: data.user ?? null, rights: data.rights ?? null }
}

// ─────────── ядро: эмуляция Bitrix REST ───────────
/** Как BFF присылает пометку о суженной выборке (`_scope` в web/bff/main.py). */
type RawScope = { limited?: boolean; partial?: boolean | string; note?: string | null }

/** Сырой вызов ядра. Возвращает как есть, включая маркеры _stale и _scope. */
type BxRaw<T> = {
  result?: T
  total?: number
  next?: number
  _stale?: boolean
  age_min?: number
  _scope?: RawScope
}

const bx = <T>(method: string, params: ListParams | Record<string, unknown>) =>
  request<BxRaw<T>>('/api/bx', { method: 'POST', body: JSON.stringify({ method, params }) })

/**
 * Пометка «выборка сужена правами» из ответа BFF.
 *
 * Текст берём ГОТОВЫЙ и своего не сочиняем: BFF пишет по-русски, чем именно сужена зона.
 * Он кладёт фразу в `note`, а в `partial` — признак «сужено частично», но часть ответов
 * присылает туда ту же фразу; принимаем оба вида, чтобы пометка не потерялась из-за формы.
 *
 * Пустая пометка (`limited` нет и текста нет) — это не пометка: возвращаем undefined, иначе
 * экран показал бы оговорку без содержания на каждом обычном ответе.
 */
function scopeOf(raw: { _scope?: RawScope }): ScopeNote | undefined {
  const s = raw._scope
  if (!s) return undefined
  const note = [s.note, s.partial]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find(Boolean) || ''
  if (!s.limited && !note) return undefined
  return { limited: s.limited !== false, note }
}

/**
 * Приводим ответ ядра к нормальному виду для UI.
 *
 * `scope` дописываем только когда он есть: лишний ключ со значением undefined ломал бы
 * сравнение ответов в кэше react-query и читался бы как «пометка была».
 */
function toList<T>(raw: BxRaw<T[]>): ListResult<T> {
  const scope = scopeOf(raw)
  const пометки = scope ? { scope } : {}
  if (raw._stale) {
    return { items: [], total: 0, stale: true, ageMin: raw.age_min, ...пометки }
  }
  return { items: raw.result || [], total: raw.total ?? 0, next: raw.next, ...пометки }
}

// ─────────── своя база: те же списки и карточки, другой адрес ───────────
//
// Куда идёт запрос, решают источник() и перевод параметров (api/istochnik.ts, api/own.ts).
// Формы ОТВЕТОВ /api/own/* повторяют /api/bx дословно (crm_own_api.py написан под это),
// поэтому toList и типы ниже общие для обоих источников. Непереводимый запрос — про то,
// чего в своей базе нет как данных, — уходит в зеркало (причины названы в own.ts).

const ownList = <T>(путь: string, параметры: string) =>
  request<BxRaw<T[]>>(`/api/own/${путь}${параметры ? `?${параметры}` : ''}`)

/** Карточка из своей базы. 404 (_core_miss) переводим в ту же ошибку, что у зеркала. */
const ownGet = async <T>(путь: string, id: string | number, ктоНеНайден: string): Promise<T> => {
  try {
    const r = await request<BxRaw<T>>(`/api/own/${путь}?id=${encodeURIComponent(String(id))}`)
    if (!r.result) throw new ApiError(ктоНеНайден, 404)
    return r.result
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) throw new ApiError(ктоНеНайден, 404)
    throw e
  }
}

export const listDeals = (params: ListParams = {}) => {
  if (источник() === 'own' && сделкиМожноИзСвоей()) {
    const перевод = переводСделок(params)
    if (перевод.вид === 'own') return ownList<Deal>('deals', перевод.параметры).then(toList)
  }
  return bx<Deal[]>('crm.deal.list', params).then(toList)
}

export const getDeal = (id: string | number) => {
  if (источник() === 'own' && сделкиМожноИзСвоей()) {
    return ownGet<Deal>('deal', id, 'Сделка не найдена')
  }
  return bx<Deal>('crm.deal.get', { id }).then((r) => {
    if (r._stale) throw new ApiError('Ядро отдаёт устаревшие данные — синк отстал', 503)
    if (!r.result) throw new ApiError('Сделка не найдена', 404)
    return r.result
  })
}

export const listCompanies = (params: ListParams = {}) => {
  if (источник() === 'own') {
    const перевод = переводКомпаний(params)
    if (перевод.вид === 'own') return ownList<Company>('companies', перевод.параметры).then(toList)
  }
  return bx<Company[]>('crm.company.list', params).then(toList)
}

export const getCompany = (id: string | number) => {
  if (источник() === 'own') return ownGet<Company>('company', id, 'Компания не найдена')
  return bx<Company>('crm.company.get', { id }).then((r) => {
    if (!r.result) throw new ApiError('Компания не найдена', 404)
    return r.result
  })
}

// ─────────── смарт-процессы (crm.item.*) ───────────
/**
 * Ответ у смарт-процессов другой формы, чем у сделок: записи спрятаны в `result.items`,
 * а не лежат в `result` массивом (у `crm.item.get` — в `result.item`). Плюс обязателен
 * `entityTypeId`: без него ядро отвечает `_core_unsupported`.
 */
function toItemList<T>(raw: BxRaw<{ items?: T[] }>): ListResult<T> {
  const scope = scopeOf(raw)
  const пометки = scope ? { scope } : {}
  if (raw._stale) {
    return { items: [], total: 0, stale: true, ageMin: raw.age_min, ...пометки }
  }
  const items = raw.result?.items ?? []
  // total ядро считает отдельным запросом и отдаёт рядом; без него — хотя бы длина страницы
  return { items, total: raw.total ?? items.length, next: raw.next, ...пометки }
}

/** Список записей смарт-процесса. `select` — как у Bitrix, маска UF-полей строчная: `uf_*`. */
export const listItems = <T = CrmItem>(entityTypeId: number, params: ListParams = {}) =>
  bx<{ items?: T[] }>('crm.item.list', { entityTypeId, ...params }).then((r) => toItemList<T>(r))

export const getItem = <T = CrmItem>(entityTypeId: number, id: string | number) =>
  bx<{ item?: T }>('crm.item.get', { entityTypeId, id }).then((r) => {
    if (r._stale) throw new ApiError('Ядро отдаёт устаревшие данные — синк отстал', 503)
    if (!r.result?.item) throw new ApiError('Запись не найдена', 404)
    return r.result.item
  })

export const listContacts = (params: ListParams = {}) => {
  if (источник() === 'own') {
    const перевод = переводКонтактов(params)
    if (перевод.вид === 'own') return ownList<Contact>('contacts', перевод.параметры).then(toList)
  }
  return bx<Contact[]>('crm.contact.list', params).then(toList)
}

export const getContact = async (id: string | number): Promise<Contact> => {
  if (источник() === 'own') {
    // В зеркале судно — это контакт, и карточка судна читает crm.contact.get. В своей
    // базе суда вынесены из людей, поэтому «контакта нет» — ещё не ответ: тем же id
    // спрашиваем /own/vessel, который отдаёт судно В ФОРМЕ КОНТАКТА (crm_own_api.py).
    try {
      return await ownGet<Contact>('contact', id, 'Контакт не найден')
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) throw e
      return ownGet<Contact>('vessel', id, 'Контакт не найден')
    }
  }
  return bx<Contact>('crm.contact.get', { id }).then((r) => {
    if (!r.result) throw new ApiError('Контакт не найден', 404)
    return r.result
  })
}

// ─────────── запись сделок (через Bitrix, ядро делает write-through) ───────────
export const createDeal = (fields: Record<string, unknown>, dry = false) =>
  request<{ ok: boolean; id: number; deal?: Deal }>('/api/deal/create', {
    method: 'POST',
    body: JSON.stringify({ fields, dry }),
  })

export const updateDeal = (id: string | number, fields: Record<string, unknown>, dry = false) =>
  request<{ ok: boolean; id: number; updated: string[]; deal?: Deal }>('/api/deal/update', {
    method: 'POST',
    body: JSON.stringify({ id, fields, dry }),
  })

/** Наши поля, которых нет в Bitrix. */
export const setDealExt = (id: string | number, patch: Record<string, unknown>) =>
  request<{ ok: boolean }>('/api/deal/ext', {
    method: 'POST',
    body: JSON.stringify({ id, patch }),
  })

// ─────────── прочее ───────────
export const searchVessels = (q: string) => {
  // Формы витрины совпадают ({rows: [...]}, порог 2 знака, предел 20 строк) — здесь
  // перевод не нужен, меняется только адрес.
  const путь = источник() === 'own' ? '/api/own/vessels' : '/api/vessels'
  return request<{ rows: Vessel[] }>(`${путь}?q=${encodeURIComponent(q)}`).then((r) => r.rows)
}

// Поиск сущностей для выпадающего выбора собирается в api/hooks.ts (useEntitySearch):
// там результаты приводятся к одному виду EntityRef, а здесь остаётся только транспорт.

export const fetchHealth = () => request<HealthInfo>('/api/health')

/** Справочники: сотрудники, воронки, стадии. Ядро кеширует их у себя на часы. */
export const fetchRefs = () => request<RefsData>('/api/refs')

/**
 * Справочник полей Bitrix по одной области (`company`, `deal`, `item:134`…).
 * Область обязательна: без неё ядро отдаёт все 3 753 поля (на 28.07.2026).
 */
export const fetchFields = (entity: string) =>
  request<FieldsData>(`/api/fields?entity=${encodeURIComponent(entity)}`)
